import { formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { Party } from '@amfam/shared/models/lib/party/party';
import { PartyEmail } from '@amfam/shared/models/lib/party/party-email';
import type { PartyPhoneUsageCode } from '@amfam/shared/models/lib/party/party-phone';
import { PartyPhone } from '@amfam/shared/models/lib/party/party-phone';
import { selectMdmId, selectMyAccountEmail } from '@amfam/shared/user/lib/+state/user.selectors';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';

import { UpdateMembershipPayload } from '../+state/profile.actions';
import { EmailPayloadToGetValidationStatus } from '../models/profile.model';

@Injectable({
  providedIn: 'root'
})
export class ProfileUtilService {
  private store = inject(Store);
  private readonly mdmId = toSignal(this.store.select(selectMdmId));
  private http = inject(HttpClient);
  private applicationService = inject(ApplicationService);
  private readonly isAdminApp = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);

  public flattenPhones(phones: PartyPhone[]) {
    const flatPhones: PartyPhone[] = [];
    for (const phone of phones) {
      for (const contactMethodUsage of phone.contactMethodUsages) {
        const contactMethodUsages = new Array<{
          typeOfUsageCode: PartyPhoneUsageCode;
          descriptionForOtherType?: string;
        }>();
        contactMethodUsages.push(contactMethodUsage);
        const flatPhone = {
          ...phone,
          tenDigitPhoneNumber: phone.tenDigitPhoneNumber,
          contactMethodUsages: contactMethodUsages
        };
        flatPhones.push(flatPhone);
      }
    }
    return flatPhones;
  }

  public getOldAndNewPrimaryEmail(selectedEmail: PartyEmail, payload: Party) {
    let data: EmailPayloadToGetValidationStatus | undefined;
    this.store
      .select(selectMyAccountEmail)
      .pipe(
        filter(email => !!email),
        take(1)
      )
      .subscribe(currentMyAccountEmail => {
        // This is to know the emailId as selectedEmail don't have.
        const futureMyAccountEmail = payload.emails?.find(
          email => selectedEmail.emailAddress === email.emailAddress
        );

        data = this.getOldAndNewPrimaries(currentMyAccountEmail, futureMyAccountEmail);
      });
    return data;
  }

  public getOldAndNewPrimaries(
    currentMyAccountEmail: PartyEmail | undefined,
    futureMyAccountEmail: PartyEmail | undefined
  ): EmailPayloadToGetValidationStatus {
    const oldPrimary: PartyEmail | undefined = !currentMyAccountEmail
      ? undefined
      : {
          emailAddress: currentMyAccountEmail.emailAddress,
          emailId: currentMyAccountEmail.emailId,
          contactMethodUsages: currentMyAccountEmail.contactMethodUsages
        };

    const newPrimary: PartyEmail = {
      emailAddress: futureMyAccountEmail?.emailAddress ?? '',
      emailId: futureMyAccountEmail?.emailId,
      contactMethodUsages: futureMyAccountEmail?.contactMethodUsages ?? []
    };
    return {
      newEntry: newPrimary,
      oldEntry: oldPrimary,
      type: 'MYACCOUNT'
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editMembership(payload: UpdateMembershipPayload[]): Observable<any>[] {
    const result: Observable<unknown>[] = [];
    let url: string;

    for (const membership of payload) {
      if (this.isAdminApp) {
        const mdmId = this.mdmId();
        url =
          env.policyExperienceApi +
          `${membership.policyNumber}/costcoMembership` +
          (mdmId ? `/?mdmId=${mdmId}` : '');
      } else {
        url = env.policyExperienceApi + `${membership.policyNumber}/costcoMembership`;
      }

      const body = {
        membershipNumber: membership.membershipNumber,
        membershipLevel: membership.membershipType,
        policySourceSystemName: membership.sourcePath,
        changeEffectiveDate: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),
        namedInsured: {
          firstName: membership.firstName,
          lastName: membership.lastName
        },
        audit: {
          requestedBy: `${membership.firstName} ${membership.lastName}`,
          requestedDate: formatDate(new Date(), 'yyyy-MM-dd', 'en-US')
        }
      };

      result.push(this.http.post(url, body));
    }

    return result;
  }
}
